import React, { useMemo } from 'react'
import { Box, CircularProgress, Container, Typography, makeStyles } from '@material-ui/core'
import { useParams } from 'react-router-dom'
import ViewTabs from '../../../../organisms/AdvisorView/components/AdvisorViewTabs'
import { usePolicies } from '../../../../../hooks'
import { useGetRegistration } from '../../../../../api/registrations'
import EmptySection from '../../../../atoms/EmptySection'
import GeneralInfoTab from './GeneralInfoTab'
import AccountsAssignedTab from './AccountsAssignedTab'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '1rem 2rem'
  },
  header: {
    paddingTop: '1rem',
    paddingBottom: '0.5rem'
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 600
  },
  subtitle: {
    fontSize: '0.875rem',
    color: 'var(--color-text-tertiary)'
  },
  centerBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '320px'
  },
  tabs: {
    paddingTop: '0.75rem'
  }
}))

const isNotFound = (error) =>
  error?.response?.status === 404 ||
  error?.status === 404 ||
  /not.?found/i.test(error?.message ?? '')

const tabOptions = {
  general: { value: 'general', label: 'General Info' },
  accounts: { value: 'accounts', label: 'Accounts Assigned' }
}

const useTabOptions = () => {
  const policies = usePolicies()
  return useMemo(() => {
    const result = {}
    // Both tabs are gated by the same admin_view_accts policy that already
    // gates the parent route. Re-checking here is defensive but cheap.
    if (policies.admin_view_accts) {
      result.general = tabOptions.general
      result.accounts = tabOptions.accounts
    }
    return result
  }, [policies])
}

function RegistrationDetailsView () {
  const classes = useStyles()
  const { orionFirmId, orionRegistrationId } = useParams()
  const tabs = useTabOptions()
  const { data, isLoading, isError, error } = useGetRegistration(
    orionFirmId,
    orionRegistrationId
  )

  if (isLoading) {
    return (
      <Container maxWidth={false} className={classes.container}>
        <Box className={classes.centerBox}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (isError && isNotFound(error)) {
    return (
      <Container maxWidth={false} className={classes.container}>
        <EmptySection
          title='Registration not found'
          description={`No registration with ID ${orionRegistrationId} was found in firm ${orionFirmId}, or it isn't visible to your firm.`}
        />
      </Container>
    )
  }

  if (isError) {
    return (
      <Container maxWidth={false} className={classes.container}>
        <EmptySection
          title='Could not load registration'
          description={error?.message ?? 'An unexpected error occurred. Please try again.'}
        />
      </Container>
    )
  }

  const registration = data?.registration
  if (!registration) {
    return (
      <Container maxWidth={false} className={classes.container}>
        <EmptySection
          title='Registration not available'
          description='No data was returned for this registration.'
        />
      </Container>
    )
  }

  return (
    <Container maxWidth={false} className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          {registration.longName || `Registration ${registration.orionRegistrationId}`}
        </Typography>
        <Typography className={classes.subtitle}>
          {`Orion Firm ${registration.orionFirmId} · Registration ${registration.orionRegistrationId}`}
        </Typography>
      </Box>
      <ViewTabs.Group tabOptions={tabs}>
        <Box className={classes.tabs}>
          <ViewTabs.Links tabsKey='admin_registration_details' />
        </Box>
        {tabs.general ? (
          <ViewTabs.Tab value='general'>
            <GeneralInfoTab registration={registration} />
          </ViewTabs.Tab>
        ) : null}
        {tabs.accounts ? (
          <ViewTabs.Tab value='accounts'>
            <AccountsAssignedTab
              orionFirmId={orionFirmId}
              orionRegistrationId={orionRegistrationId}
            />
          </ViewTabs.Tab>
        ) : null}
      </ViewTabs.Group>
    </Container>
  )
}

export default RegistrationDetailsView
